import { createRouter, createWebHashHistory } from 'vue-router';
import type { RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/components/HomeScreen.vue'),
    meta: { requiresAuth: false } // Auth is handled by App.vue
  },
  {
    path: '/entity/:entityName',
    name: 'entity-manager',
    component: () => import('@/components/EntityManager.vue'),
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/compare/:entityName',
    name: 'environment-comparison',
    component: () => import('@/components/EnvironmentComparison.vue'),
    props: true,
    meta: { requiresAuth: true }
  },
  {
    path: '/import',
    name: 'service-import',
    component: () => import('@/components/ServiceImport.vue'),
    meta: { 
      requiresAuth: true,
      requiresPermission: 'canAccessImport'
    }
  },
  {
    path: '/wizard',
    name: 'mapping-wizard',
    component: () => import('@/components/MappingWizard.vue'),
    meta: {
      requiresAuth: true,
      requiresPermission: 'canAccessWizard'
    }
  },
  {
    path: '/mapping-validation',
    name: 'mapping-validation',
    component: () => import('@/components/MappingValidation.vue'),
    meta: {
      requiresAuth: true,
      requiresPermission: 'canAccessMappingValidation'
    }
  },
  {
    path: '/onboarding-copy',
    name: 'product-onboarding-copy',
    component: () => import('@/components/ProductOnboardingCopy.vue'),
    meta: {
      requiresAuth: true,
      requiresPermission: 'canAccessOnboarding'
    }
  },
  {
    path: '/function-test',
    name: 'function-test',
    component: () => import('@/components/FunctionTestTool.vue'),
    meta: { requiresAuth: true }
  }
];

const router = createRouter({
  history: createWebHashHistory(),
  routes
});

// Navigation guard for authentication
router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();
  
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'home' });
    return;
  }
  
  // Check specific permissions
  if (to.meta.requiresPermission) {
    const permission = to.meta.requiresPermission as string;
    
    // Permission check logic
    if (permission === 'canAccessImport') {
      // Only admin and deployment roles can access import
      if (!authStore.isAdmin && !authStore.isDeployment) {
        next({ name: 'home' });
        return;
      }
    }

    if (permission === 'canAccessWizard') {
      // Only developer and admin roles can access the mapping wizard
      if (!authStore.isDeveloper && !authStore.isAdmin) {
        next({ name: 'home' });
        return;
      }
    }

    if (permission === 'canAccessMappingValidation') {
      // Only developer and admin roles can access mapping validation
      if (!authStore.isDeveloper && !authStore.isAdmin) {
        next({ name: 'home' });
        return;
      }
    }

    if (permission === 'canAccessOnboarding') {
      // Only developer and admin roles can access product onboarding
      if (!authStore.isDeveloper && !authStore.isAdmin) {
        next({ name: 'home' });
        return;
      }
    }
  }
  
  next();
});

export default router;
